/**
 * Centralized Logger Service
 * Handles all logging with environment-aware filtering
 * In production: sends to Sentry
 * In development: logs to console
 */

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

interface LogContext {
  [key: string]: any;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private isProduction = process.env.NODE_ENV === 'production';

  /**
   * Debug log - only in development
   * Accepts variable arguments: debug(message, arg1, arg2, ...)
   */
  debug(...args: any[]): void {
    if (this.isDevelopment) {
      console.debug('[DEBUG]', ...args);
    }
  }

  /**
   * Info log - always logged
   * Accepts variable arguments: info(message, arg1, arg2, ...)
   */
  info(...args: any[]): void {
    if (this.isDevelopment) {
      console.info('[INFO]', ...args);
    }
    // In production, only send important info to monitoring
    if (this.isProduction && args.length > 0 && typeof args[args.length - 1] === 'object' && args[args.length - 1]?.severity === 'high') {
      const message = typeof args[0] === 'string' ? args[0] : 'Info';
      this.sendToMonitoring('info', message, args[args.length - 1]);
    }
  }

  /**
   * Warning log
   * Accepts variable arguments: warn(message, arg1, arg2, ...)
   */
  warn(...args: any[]): void {
    if (this.isDevelopment) {
      console.warn('[WARN]', ...args);
    }
    const message = args.length > 0 && typeof args[0] === 'string' ? args[0] : 'Warning';
    this.sendToMonitoring('warning', message, undefined);
  }

  /**
   * Error log - ALWAYS sent to monitoring in production
   * Accepts variable arguments: error(message, arg1, arg2, ...)
   */
  error(...args: any[]): void {
    let message: string = 'Error occurred';
    let errorMessage: string | undefined;
    let stack: string | undefined;

    // Parse first argument for message/error
    if (args.length > 0) {
      if (typeof args[0] === 'string') {
        message = args[0];
        // Check if second arg is Error
        if (args[1] instanceof Error) {
          errorMessage = args[1].message;
          stack = args[1].stack;
        }
      } else if (args[0] instanceof Error) {
        errorMessage = args[0].message;
        stack = args[0].stack;
      } else {
        errorMessage = String(args[0]);
      }
    }

    if (this.isDevelopment) {
      console.error('[ERROR]', ...args);
    }

    // Always send to monitoring
    this.sendToMonitoring('error', message, {
      errorMessage,
      stack,
    });
  }

  /**
   * Fatal error - immediate action needed
   * Accepts variable arguments: fatal(message, arg1, arg2, ...)
   */
  fatal(...args: any[]): void {
    let message: string = 'Fatal error occurred';
    let errorMessage: string | undefined;
    let stack: string | undefined;

    // Parse first argument for message/error
    if (args.length > 0) {
      if (typeof args[0] === 'string') {
        message = args[0];
        // Check if second arg is Error
        if (args[1] instanceof Error) {
          errorMessage = args[1].message;
          stack = args[1].stack;
        }
      } else if (args[0] instanceof Error) {
        errorMessage = args[0].message;
        stack = args[0].stack;
      } else {
        errorMessage = String(args[0]);
      }
    }

    if (this.isDevelopment) {
      console.error('[FATAL]', ...args);
    }

    this.sendToMonitoring('fatal', message, {
      errorMessage,
      stack,
    });
  }

  /**
   * Send to monitoring service (Sentry, DataDog, etc)
   * This method will be enhanced when Sentry is configured
   */
  private sendToMonitoring(
    level: string,
    message: string,
    context?: LogContext
  ): void {
    // Will be implemented with Sentry integration
    // For now, just log to console in development
    if (this.isDevelopment) {
      console.debug(
        `[MONITORING] ${level.toUpperCase()}: ${message}`,
        context
      );
    }
  }
}

export const logger = new Logger();
